import React from "react";

import { colors as colorTokens } from "@zapalacorp/tokens";
import { ChartDataset } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import { OverviewChartColors } from "../../../../constants";
import { IDashboardData } from "../../../../models/overview/dashboard";
import NotEnoughData from "../../../DashMetrics/NotEnoughData";

const LIMIT_OF_CATEGORIES = 5;

interface Props {
  colors?: string[];
  datasets: IDashboardData[];
  labels: string[];
}

const OverviewDoughnutGraph = ({ colors = OverviewChartColors, datasets, labels }: Props) => {
  const { t: translate } = useTranslation();
  const { length } = datasets[0].data;

  if (!length) return <NotEnoughData />;

  const options = {
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { padding: 8 }, position: "right" as const },
    },
    responsive: true,
  };

  const total = (datasets[0].data as number[]).reduce((total, value) => total + value, 0);
  const percentages: number[] = new Array(LIMIT_OF_CATEGORIES + 1).fill(0); // +1 because of "Others" category.

  for (let i = 0; i < length; i += 1) {
    const value = datasets[0].data[i] as number;

    const percentage = total === 0 ? 0 : (value / total) * 100;

    if (i < LIMIT_OF_CATEGORIES) {
      percentages[i] = percentage;
    } else {
      percentages[LIMIT_OF_CATEGORIES] += percentage;
    }
  }

  if (length > LIMIT_OF_CATEGORIES) {
    labels = [...labels.slice(0, LIMIT_OF_CATEGORIES), translate("others").toUpperCase()];
    colors = [...colors.slice(0, LIMIT_OF_CATEGORIES), colorTokens.gray300];
  }

  return (
    <Doughnut
      data={{
        datasets: datasets.map((dataset) => ({
          ...dataset,
          backgroundColor: colors,
        })) as ChartDataset<"doughnut", number[]>[],
        labels: labels.map((label, idx) => `${label} ${percentages[idx].toFixed(2)}%`),
      }}
      options={options}
    />
  );
};

export default OverviewDoughnutGraph;
